import { addToast, removeToast, removeToastsByMessage } from '@src/store/slices/toastSlice';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

type PdfStatus = {
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
};

type PdfToastMessages = {
  pending: string;
  success: string;
  error?: string;
};

const DEFAULT_ERROR_MESSAGE = 'PDF作成中にエラーが発生しました';

/**
 * PDF生成の進捗に応じてトースト通知を出し分ける共通フック。
 */
export const usePdfToastNotifications = (status: PdfStatus, messages: PdfToastMessages): void => {
  const dispatch = useDispatch();
  const pendingToastIdRef = useRef<string | null>(null);
  const successToastIdRef = useRef<string | null>(null);
  const previousStatusRef = useRef<PdfStatus>({
    isPending: false,
    isSuccess: false,
    isError: false,
  });

  useEffect(() => {
    const previousStatus = previousStatusRef.current;

    if (status.isPending && !previousStatus.isPending) {
      dispatch(removeToastsByMessage(messages.pending));
      if (successToastIdRef.current) {
        dispatch(removeToast(successToastIdRef.current));
        successToastIdRef.current = null;
      }
      const toast = dispatch(
        addToast({
          message: messages.pending,
          severity: 'info',
          persistent: true,
        })
      );
      pendingToastIdRef.current = toast.payload.id;
    }

    if (status.isSuccess && !previousStatus.isSuccess) {
      dispatch(removeToastsByMessage(messages.pending));
      dispatch(removeToastsByMessage(messages.success));
      if (pendingToastIdRef.current) {
        dispatch(removeToast(pendingToastIdRef.current));
        pendingToastIdRef.current = null;
      }

      const toast = dispatch(
        addToast({
          message: messages.success,
          severity: 'success',
          duration: 3000,
        })
      );
      successToastIdRef.current = toast.payload.id;
    }

    if (status.isError && !previousStatus.isError) {
      dispatch(removeToastsByMessage(messages.pending));
      dispatch(removeToastsByMessage(messages.success));
      if (pendingToastIdRef.current) {
        dispatch(removeToast(pendingToastIdRef.current));
        pendingToastIdRef.current = null;
      }

      dispatch(
        addToast({
          message: messages.error ?? DEFAULT_ERROR_MESSAGE,
          severity: 'error',
          duration: 5000,
        })
      );
    }

    previousStatusRef.current = {
      isPending: status.isPending,
      isSuccess: status.isSuccess,
      isError: status.isError,
    };
  }, [
    status.isPending,
    status.isSuccess,
    status.isError,
    messages.pending,
    messages.success,
    messages.error,
    dispatch,
  ]);
};
